import { Drawer, Tooltip } from "antd";
import React, { useEffect, useState } from "react";
import CustomAvatar from "../../../Core/Common/CustomAvatar";
import { ChevronIcon } from "../../../Core/svgV2/Chevron";
import CopyButton from "../../../Core/CommonV2/CopyButton";
import dayjs from "dayjs";
import useTimezone from "../../../Core/hooks/useTimezone";
import { useAppDispatch, useAppSelector } from "../../../Core/redux/hooks";
import {
  updateConversations,
  updateThreadForUser,
} from "../../../Core/redux/slices/conversations.slice";
import { muteThread } from "../../../Core/redux/api/v3/conversationAPI";
import { SearchElongatedIcon } from "../../../Core/svgV2/SearchIcon";
import { PastRequestIcon } from "../../../Core/svgV2/PastRequestIcon";
import { GroupUserIcon } from "../../../Core/svgV2/UserIcon";
import StarIcon from "../../../Core/svgV3/StarIcon";
import SpeakerMuteIcon from "../../../Core/svgV3/SpeakerMuteIcon";
import { UnmuteIcon } from "../../../Core/svgV3/UnmuteIcon";
import { SpeakerWaveFilledIcon } from "../../../Core/svgV3/SpeakerWaveFilledIcon";
import { TwoPersonFilledIcon } from "../../../Core/svgV2/TwoPersonFilledIcon";

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

const QuickActionButton = ({ onPress, icon, label, disabled = false }) => (
  <button
    onClick={disabled ? undefined : onPress}
    disabled={disabled}
    aria-disabled={disabled}
    className={`flex-1 h-[72px] rounded-[10px] border border-[#EDF3F9] bg-[#F9FBFE] flex flex-col items-center justify-center gap-1 transition-colors ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
      }`}
  >
    {icon}
    <span className="out-500-12 text-gray-700 whitespace-nowrap leading-[18px]">{label}</span>
  </button>
);

// Figma: row is 318 Fill x 56 Fill, padding top/bottom 8px, left 16px, right 24px, gap 16px.
// Leading avatar/icon is 40 Hug x 40 Hug.
// NOTE: getCommonThreadsAPI returns the field as `profilePicture` (confirmed
// from the actual response payload), not `groupProfilePicture` — CustomAvatar
// only falls back to initials when `src` is falsy, so once this is the right
// key groups with a photo render it instead of the colored-initial circle.
const GroupRow = ({ group, onClick }) => (
  <button onClick={onClick} className="flex items-center gap-x-4 pl-4 pr-6 py-2 w-full text-left">
    <CustomAvatar
      title={group.name || group.groupName}
      src={group.profilePicture}
      size={40}
      whiteText
      color={group.threadProfilePictureColorCode}
    />
    <div className="min-w-0 flex-1">
      <p className="out-600-16 text-[#1D1B20] truncate">{group.name || group.groupName}</p>
      <p className="out-300-12 text-gray-500 truncate">
        {(group.participants || []).slice(0, 3).join(", ")}
        {group.participants?.length > 3 ? "..." : ""}
      </p>
    </div>
  </button>
);

const UserDetailsDrawer = ({ open = false, onClose, user, onSearchClick }) => {
  const timezone = useTimezone(user.lat, user.long);
  const dispatch = useAppDispatch();
  const { currentThreadMessage } = useAppSelector((state) => state.conversations);
  const { user: loggedInUser } = useAppSelector((state) => state.userDetails);

  const otherUserId = user.userId || user.userid || user.id;
  const threadId = user.threadId;

  const threadMedia = useAppSelector((state) =>
    threadId ? state.conversations.threadMedia?.[String(threadId)] : null
  );
  const liveThread = useAppSelector((state) =>
    threadId
      ? state.conversations.threads.find((t) => String(t.threadId) === String(threadId))
      : null
  );

  const isMuted = (liveThread ? liveThread.isMute : user.isMute) == 1;

  // Prefetched by ChatWindow the moment this private thread was opened — no
  // API call (and therefore no lag) here, this just reads the cached result.
  const commonGroups = useAppSelector((state) =>
    otherUserId ? state.conversations.commonThreads?.[String(otherUserId)] ?? [] : []
  );

  const sharedFiles = React.useMemo(() => {
    if (!threadMedia) return [];
    return [...(threadMedia.media || []), ...(threadMedia.documents || [])];
  }, [threadMedia]);

  const handleOpenSharedFiles = () => {
    dispatch(updateConversations({ key: "sharedFilesDrawer", value: true }));
  };

  const handleMuteChat = async () => {
    const nextIsMute = +!isMuted;

    dispatch(
      updateThreadForUser({
        threadId,
        userId: otherUserId,
        isMute: nextIsMute,
        isThread: threadId,
      })
    );

    try {
      await muteThread({ chatId: threadId });
    } catch (e) {
      console.error("mute toggle failed:", e);
      dispatch(
        updateThreadForUser({
          threadId,
          userId: otherUserId,
          isMute: isMuted ? 1 : 0,
          isThread: threadId,
        })
      );
    }
  };

  const handleSearchClick = () => {
    onSearchClick?.();
    onClose?.();
  };

  const attachmentCount = sharedFiles.length;

  return (
    <Drawer
      title={null}
      closable={false}
      placement="right"
      open={open}
      width={"318px"}
      onClose={onClose}
    >
      <div className="flex flex-col h-full relative overflow-y-auto">
        {/* chevron.backward per Figma — base icon points right, rotate-180 to face left */}
        <button onClick={onClose} className="absolute top-4 left-4 z-10">
          <ChevronIcon rotate="right" className="text-primary-700 rotate-90" />
        </button>

        <div className="flex flex-col gap-5 pt-4 pr-4 pb-2 pl-4">
          {/* Identity */}
          <div className="flex w-full justify-center flex-col items-center">
            <CustomAvatar
              size={120}
              title={`${user.firstname} ${user.lastname}`}
              src={user.profilePicture}
              whiteText
              fontSize="40px"
              color={user.profilePictureColorCode}
            />
            <p className="out-600-24 text-[#292927] mt-2.5 text-center">
              {user.firstname} {user.lastname}
            </p>
            {user.jobTitle && <p className="out-400-14 text-gray-500 mt-0.5">{user.jobTitle}</p>}
            {user.company && <p className="out-500-12 text-black">{user.company}</p>}
          </div>

          {/* Quick actions */}
          <div className="flex items-stretch justify-between gap-x-2">
            <QuickActionButton
              icon={<PastRequestIcon className="w-5 h-5" />}
              label="Requests"
              disabled
            />
            <QuickActionButton
              icon={<StarIcon filled className="text-yellow-400" width={24} height={24} />}
              label="Starred"
              onPress={() => {/* TODO: wire to starred-messages view once it exists on web */ }}
            />
            <QuickActionButton
              icon={<SearchElongatedIcon className="text-primary-600 w-6 h-6" />}
              label="Search"
              onPress={handleSearchClick}
            />
            <QuickActionButton
              icon={
                isMuted ? (
                  <SpeakerMuteIcon className="text-primary-600 w-5 h-5" />
                ) : (
                  <SpeakerWaveFilledIcon className="text-primary-600 w-6 h-5" />
                )
              }
              label={isMuted ? "Unmute" : "Mute"}
              onPress={handleMuteChat}
            />
          </div>

          {/* Shared files */}
          <div>
            <div className="flex items-center justify-between py-2">
              <p className="out-600-14 text-black">Shared files ({attachmentCount})</p>
              {attachmentCount > 0 && (
                <button onClick={handleOpenSharedFiles} className="out-500-14 text-primary-600">
                  View all
                </button>
              )}
            </div>
            {attachmentCount > 0 && (
              <div className="grid grid-cols-4 gap-2">
                {sharedFiles.slice(0, 4).map((file, i) => {
                  const isImage = file.mimeType?.includes("image") || file.type === "image";
                  const duration = formatDuration(file.duration);
                  return (
                    <button
                      key={i}
                      onClick={handleOpenSharedFiles}
                      className="relative w-full aspect-square rounded-[6px] overflow-hidden bg-gray-100"
                    >
                      {isImage ? (
                        <img src={file.url} className="w-full h-full object-cover" />
                      ) : (
                        <img
                          src="/images/v2/conversations/file.svg"
                          className="w-full h-full object-contain p-3"
                        />
                      )}
                      {duration && (
                        <span className="absolute bottom-1 right-1 text-white text-[10px] leading-none bg-black/50 rounded px-1 py-0.5">
                          {duration}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Groups in common */}
          <div className="-mx-4">
            <div className="flex items-center justify-between px-4 pb-2">
              <p className="out-600-14 text-black">{commonGroups.length} Groups in common</p>
              {/* <SearchElongatedIcon className="text-gray-400 w-[18px] h-[18px]" /> */}
            </div>

            {/* Figma: row is 318 Fill x 56 Fill, padding top/bottom 8px, left 16px, right 24px, gap 16px.
                Leading icon container and icon inside it are both 40 Hug x 40 Hug — icon must NOT
                exceed the container. */}
            <button
              className="flex items-center gap-x-4 pl-4 pr-6 py-2 w-full text-left"
              onClick={() => {/* TODO: wire to new-group flow, existingMember: otherUserId */ }}
            >
              <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center shrink-0">
                <TwoPersonFilledIcon className="text-primary-600 w-40 h-40" />
              </div>
              <span className="out-600-14 text-primary-600">Create group with {user.firstname}</span>
            </button>

            {commonGroups.map((group) => (
              <GroupRow
                key={group.threadId ?? group.chat}
                group={group}
                onClick={() => {/* TODO: navigate to that group thread */ }}
              />
            ))}
          </div>

          {/* Contact details — not present in the Figma crops sent so far.
              Kept for now; tell me if this section should be removed. */}
          {/* <div className="flex flex-col gap-4 pb-2">
            <div>
              <p className="out-300-12 text-gray-500">Email ID</p>
              <div className="mt-1.5 flex items-center justify-between">
                <p className="out-500-12 text-black">{user.email || "N/A"}</p>
                <CopyButton className="out-500-14 text-primary-700" height="20px" text={user.email} />
              </div>
            </div>
            <div>
              <p className="out-300-12 text-gray-500">Mobile number</p>
              <p className="out-500-12 text-black mt-2">{user.mobile || "N/A"}</p>
            </div>
            <div>
              <p className="out-300-12 text-gray-500">Currently located in</p>
              <div className="flex justify-between items-center mt-2 flex-wrap gap-y-1">
                <p className="out-500-12 text-black">{user.location || "N/A"}</p>
                <div className="flex items-center">
                  <img src="/images/v2/conversations/schedule.svg" />
                  <p className="out-300-12 text-gray-500 ml-2">
                    {timezone.timeZoneId ? dayjs().tz(timezone.timeZoneId).format("hh:mm a") : "N/A"}{" "}
                    Local time
                  </p>
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </Drawer>
  );
};

export default UserDetailsDrawer;